import React, { useCallback } from "react";
import { connect } from "react-redux";

import { get } from "lodash-es";
import { defineMessages, useIntl } from "react-intl";

import { Lead } from "@mapmycustomers/shared/types/entity";
import { isEmptyString } from "@mapmycustomers/shared/util/stringUtils";

import Call from "@app/component/actions/Call";
import StreetView from "@app/component/actions/StreetView";
import VisitWebsite from "@app/component/actions/VisitWebsite";
import { isLeadFinderBasic } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";

import HideLead from "./HideLead";
import styles from "./LeadFinderItemActions.module.scss";

interface Props {
  basicPlan: boolean;
  disableEmpty?: boolean;
  entity: Lead;
}

const messages = defineMessages({
  call: {
    id: "map.tool.leadFinder.actions.call",
    defaultMessage: "Quick call not supported on Lead Finder Basic",
    description: "Lead Finder call action - Basic plan notice",
  },
  website: {
    id: "map.tool.leadFinder.actions.website",
    defaultMessage: "Website links not supported on Lead Finder Basic",
    description: "Lead Finder website action - Basic plan notice",
  },
});

const LeadFinderItemActions: React.FC<Props> = ({ basicPlan, disableEmpty = false, entity }) => {
  const intl = useIntl();
  const analytics = useAnalytics();

  const phone: string = get(entity, "phone") ?? get(entity, "international_phone_number");
  const website: string = get(entity, "website");

  const hasPhone = !isEmptyString(phone);
  const hasWebsite = !isEmptyString(website);

  const handlePhoneClick = useCallback(() => {
    analytics.clicked(["Phone number"]);
  }, [analytics]);

  const handleWebsiteClick = useCallback(() => {
    analytics.clicked(["Website"]);
  }, [analytics]);

  const handleStreetViewClick = useCallback(() => {
    analytics.clicked(["Street View"]);
  }, [analytics]);

  return (
    <div className={styles.actions}>
      <Call
        disabled={basicPlan ?? (!hasPhone && disableEmpty)}
        onClick={handlePhoneClick}
        phoneNumber={basicPlan ? undefined : phone}
        tooltip={basicPlan ? intl.formatMessage(messages.call) : undefined}
      />
      <VisitWebsite
        disabled={basicPlan ?? (!hasWebsite && disableEmpty)}
        onClick={handleWebsiteClick}
        tooltip={basicPlan ? intl.formatMessage(messages.website) : undefined}
        url={basicPlan ? undefined : website}
      />
      <StreetView disabled={basicPlan} lead={entity} onClick={handleStreetViewClick} />
      <HideLead entity={entity} />
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  basicPlan: isLeadFinderBasic(state),
});

export default connect(mapStateToProps)(LeadFinderItemActions);
